/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import translate from 'services/i18n/translate';
import { Seconds } from '@wit/vtv-x-foundation-lib';
import { convertHMS } from 'services/utils/DateTimeUtils';
import { useSelector } from 'react-redux';
import { Selectors as PlayerSelectors } from 'store/ducks/player';
import { ButtonOption } from 'types/components/SelectOptions';
import VolumeController from './VolumeController';
import VideoRangeSlider from './VideoRangeSlider';
import { ImageLoader, Modal } from '../..';
import UpNext from './UpNext';
import Constants from '../../../../configs/Constants';
import { AudioTrackKind, TextTrackKind, TextTrackMode } from '../../../../types/libs/opySdk';
import StatsForNerds, { IPlayerStats } from './Stats';

enum EModalType {
  SUBTITLES = 'subtitles',
  AUDIOTRACK = 'audiotrack',
  NONE = 'none',
}
const SUBTITLES_OFF = 'off';

export interface EpisodeData {
  name: string;
  seasonNumber?: number;
  episodeNumber?: number;
  startDate?: Seconds;
  endDate?: Seconds;
}

export interface MediaData {
  controllers: {
    disable: boolean;
    play: boolean;
    restart: boolean;
    // fullscreen: boolean;
    moveScrubber: boolean;
    rules: { allowRewind: boolean; allowForward: boolean; allowPause: boolean }; // allowSubtitles: boolean };
    subtitles: [];
  };
  episode: EpisodeData;
  channel?: {
    name: string;
    logo: string;
  };
}

interface Props {
  mediaData: MediaData;
  isFullscreen: boolean;
  overlayActive: boolean;
  controllersActive: boolean;
  isPlaying: boolean;
  isMuted: boolean;
  sliderActive: boolean;
  progress: Seconds;
  duration: Seconds;
  seekingStream: boolean;
  liveProgress?: Seconds;
  subtitles: TextTrack[];
  audio: AudioTrack[];
  handleHotspot: () => void;
  handleRestartButton: () => void;
  handleVolume: (arg?: number) => Promise<number>;
  handleMute: (arg: boolean) => void;
  handleScrubberPosition: (pos: Seconds | undefined) => void;
  handleFullscreen: () => void;
  setShowWrapper: (arg: boolean) => void;
  onScrub?: (progress?: Seconds) => void;
  videoStats?: IPlayerStats;
}

function VideoUi({
  mediaData,
  isFullscreen,
  overlayActive,
  controllersActive,
  isPlaying,
  isMuted,
  sliderActive,
  progress,
  liveProgress,
  duration,
  seekingStream,
  subtitles,
  audio,
  handleHotspot,
  handleRestartButton,
  handleVolume,
  handleMute,
  handleFullscreen,
  handleScrubberPosition,
  setShowWrapper,
  onScrub,
  videoStats,
}: Props): JSX.Element {
  const audioEnabled = true;
  const subtitlesEnabled = true;
  const isLiveMode = useSelector(PlayerSelectors.isLiveMode);
  const [activeModalType, setActiveModalType] = useState<EModalType>();
  const [showAudioModal, setShowAudioModal] = useState(false);
  const [showSubtitlesModal, setShowSubtitlesModal] = useState(false);
  const [audioLabel, setAudioLabel] = useState('');
  const [hasAudioDescription, setHasAudioDescription] = useState(false);
  const [audioDescriptionEnabled, setAudioDescriptionEnabled] = useState(false);
  const [audioStreamId, setAudioStreamId] = useState<string>();
  const nagraSubtitles = useMemo(() => {
    return [...subtitles];
  }, [subtitles]);

  const nagraAudio = useMemo(() => {
    return [...audio];
  }, [audio]);
  const [subtitlesOptions, setSubtitlesOptions] = useState<ButtonOption[]>();
  const [activeSubtitleId, setActiveSubtitleId] = useState<string>();
  const currentProgress = useRef(progress);
  // Player Stats
  const activateStats = useSelector(PlayerSelectors.isPlayerStatsActivated);

  // #region AUDIO OPTIONS
  const [audioOptions, setAudioOptions] = useState<ButtonOption[]>();

  const getSubtitleId = (track: TextTrack) => {
    if (track.id.length) {
      return track.id;
    }
    // fallback for Safari as it is retuning empty string for track IDs
    return `trackIdFromObjectProperties_${track.label}_[${track.language}]_${track.kind}`;
  };

  useEffect(() => {
    if (!seekingStream && progress <= duration) {
      currentProgress.current = progress;
    }
  }, [duration, progress, seekingStream]);

  useEffect(() => {
    if (!showAudioModal) {
      // reset sudio options when the modal closes
      setAudioStreamId(undefined);
    }
  }, [showAudioModal]);

  useEffect(() => {
    if (!showSubtitlesModal) {
      // reset subtitles options when the modal closes
      for (let i = 0; i < nagraSubtitles.length; i += 1) {
        if (nagraSubtitles[i].mode === TextTrackMode.SHOWING) {
          setActiveSubtitleId(getSubtitleId(nagraSubtitles[i]));
          return;
        }
      }
      setActiveSubtitleId(undefined);
    }
  }, [nagraSubtitles, showSubtitlesModal]);

  const getAudioTranslation = (track: AudioTrack): string => {
    let translation: string | undefined;
    if (track.language) {
      const regionNames = new Intl.DisplayNames([translate.getLanguage()], { type: 'language' });
      translation = regionNames.of(track.language);
    }

    if (translation === undefined) {
      translation = track.label;
    }

    if (track.kind === AudioTrackKind.AUDIO_DESCRIPTIONS) {
      translation = `${translation} ${Constants.AUDIO_DESCRIPTION_LABEL}`;
    }

    return translation;
  };

  const parsedAudioButtonsOptions = useMemo(() => {
    return audio.map((e, index) => ({
      id: `track-${e.id ? e.id : `number-${index}`}`,
      text: getAudioTranslation(e),
      onClick: () => {
        if (e.id) {
          setAudioStreamId(e.id);
        }
      },
      selected: e.id && audioStreamId ? e.id === audioStreamId : e.enabled,
    }));
  }, [audio, audioStreamId]);

  useEffect(() => {
    setAudioOptions(parsedAudioButtonsOptions);
  }, [parsedAudioButtonsOptions]);

  // Update action of Audio Track for the Player
  const updateAudioTrack = useCallback(
    (id?: string) => {
      if (id === undefined) {
        return;
      }

      let found = false;

      for (let i = 0; i < nagraAudio.length; i += 1) {
        const e = nagraAudio[i];
        if (!found && e.id === id) {
          found = true;
          e.enabled = true;
          setAudioLabel(e.label);
        } else {
          e.enabled = false;
        }
      }
    },
    [nagraAudio]
  );

  // Get the current Index fo AUDIO of the array
  const currentAudioIndexEnabled = useMemo(() => {
    return parsedAudioButtonsOptions.map((i) => i.selected).indexOf(true);
  }, [parsedAudioButtonsOptions]);

  // KEYBOARD Binding actions
  const handleAudioTracksKeyboard = useCallback(
    (e: KeyboardEvent) => {
      if (activeModalType === EModalType.AUDIOTRACK) {
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          if (nagraAudio?.length) {
            const nextIndex = currentAudioIndexEnabled > 0 ? currentAudioIndexEnabled - 1 : currentAudioIndexEnabled;
            const updatedId = nextIndex >= 0 ? nagraAudio[nextIndex].id : '';
            if (updatedId && updatedId !== '') {
              setAudioStreamId(updatedId);
            }
          }
        }
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          if (nagraAudio?.length) {
            const nextIndex =
              currentAudioIndexEnabled < nagraAudio.length - 1
                ? currentAudioIndexEnabled + 1
                : currentAudioIndexEnabled;
            const updatedId = nextIndex >= 0 ? nagraAudio[nextIndex].id : '';
            if (updatedId && updatedId !== '') {
              setAudioStreamId(updatedId);
            }
          }
        }
        if (e.key === 'Enter') {
          updateAudioTrack(audioStreamId);
          setShowAudioModal(false);
        }
      }
    },
    [activeModalType, audioStreamId, currentAudioIndexEnabled, nagraAudio, updateAudioTrack]
  );

  // #endregion

  // #region SUBTITLES

  const getSubtitlesTranslation = (track: TextTrack): string => {
    let translation: string | undefined;
    if (track.language) {
      const regionNames = new Intl.DisplayNames([translate.getLanguage()], { type: 'language' });
      translation = regionNames.of(track.language);
    }

    if (translation === undefined) {
      translation = track.label;
    }

    if (track.kind === TextTrackKind.SUBTITLES_HARD_OF_HEARING) {
      translation = `${translation} ${Constants.SUBTITLES_HARD_OF_HEARING_LABEL}`;
    }

    return translation;
  };

  const updateSelectedSubtitleTrack = useCallback(
    (id?: string) => {
      if (id === undefined) {
        return;
      }

      let found = false;

      for (let i = 0; i < nagraSubtitles.length; i += 1) {
        if (!found && getSubtitleId(nagraSubtitles[i]) === id) {
          found = true;
          nagraSubtitles[i].mode = TextTrackMode.SHOWING;
        } else {
          nagraSubtitles[i].mode = TextTrackMode.DISABLED;
        }
      }
    },
    [nagraSubtitles]
  );

  // All Subtitles Options
  const parsedSubtitlesButtons = useMemo(() => {
    return [
      {
        id: 'off',
        text: translate.modalOptionOff,
        onClick: () => {
          setActiveSubtitleId(SUBTITLES_OFF);
        },
        selected: activeSubtitleId === undefined || activeSubtitleId === SUBTITLES_OFF,
      },
      ...nagraSubtitles.map((e) => ({
        id: getSubtitleId(e),
        text: getSubtitlesTranslation(e),
        onClick: () => {
          if (e !== undefined) {
            setActiveSubtitleId(getSubtitleId(e));
          }
        },
        selected: getSubtitleId(e) === activeSubtitleId,
      })),
    ];
  }, [activeSubtitleId, nagraSubtitles]);

  // INIT Subtitles Options
  useEffect(() => {
    setSubtitlesOptions(parsedSubtitlesButtons);
  }, [parsedSubtitlesButtons]);

  // Get the current Index fo Subtitles of the array
  const currentSubtitlesIndexEnabled = useMemo(() => {
    return parsedSubtitlesButtons.map((i) => i.selected).indexOf(true);
  }, [parsedSubtitlesButtons]);

  // KEYBOARD SUBTITLES
  const handleSubtitlesKeyBoard = useCallback(
    (e: KeyboardEvent) => {
      if (activeModalType === EModalType.SUBTITLES) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          const nextIndex =
            currentSubtitlesIndexEnabled < parsedSubtitlesButtons.length - 1
              ? currentSubtitlesIndexEnabled + 1
              : currentSubtitlesIndexEnabled;
          const updatedId = nextIndex >= 0 ? parsedSubtitlesButtons[nextIndex].id : '';

          if (updatedId && updatedId !== '') {
            setActiveSubtitleId(updatedId);
          }
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          const nextIndex =
            currentSubtitlesIndexEnabled > 0 ? currentSubtitlesIndexEnabled - 1 : currentSubtitlesIndexEnabled;
          const updatedId = nextIndex >= 0 ? parsedSubtitlesButtons[nextIndex].id : '';

          if (updatedId && updatedId !== '') {
            setActiveSubtitleId(updatedId);
          }
        }
        if (e.key === 'Enter') {
          updateSelectedSubtitleTrack(activeSubtitleId);
          setShowSubtitlesModal(false);
        }
      }
    },
    [
      activeModalType,
      activeSubtitleId,
      currentSubtitlesIndexEnabled,
      parsedSubtitlesButtons,
      updateSelectedSubtitleTrack,
    ]
  );

  // #endregion

  // #region AUDIO DESCRIPTION
  useEffect(() => {
    for (let i = 0; i < nagraAudio.length; i += 1) {
      if (nagraAudio[i].label === audioLabel && nagraAudio[i].kind === AudioTrackKind.AUDIO_DESCRIPTIONS) {
        setHasAudioDescription(true);
      }
    }
    setHasAudioDescription(false);
  }, [audioLabel, nagraAudio]);

  const toggleAudioDescriptionTrack = () => {
    for (let i = 0; i < nagraAudio.length; i += 1) {
      const e = nagraAudio[i];
      if (audioDescriptionEnabled && e.label === audioLabel) {
        e.enabled = true;
      } else if (!audioDescriptionEnabled && e.label === audioLabel && e.kind === AudioTrackKind.AUDIO_DESCRIPTIONS) {
        e.enabled = true;
      } else {
        e.enabled = false;
      }
    }
    setAudioDescriptionEnabled((state) => !state);
  };

  // #endregion

  useEffect(() => {
    window.addEventListener('keydown', handleSubtitlesKeyBoard);
    window.addEventListener('keydown', handleAudioTracksKeyboard);
    return () => {
      // Remove listener
      window.removeEventListener('keydown', handleSubtitlesKeyBoard);
      window.removeEventListener('keydown', handleAudioTracksKeyboard);
    };
  }, [handleAudioTracksKeyboard, handleSubtitlesKeyBoard]);

  return (
    <div className="video-ui" id="VideoUi">
      {activateStats ? <StatsForNerds stats={videoStats} /> : null}
      {!isLiveMode && <UpNext />}
      {isLiveMode && !isFullscreen && overlayActive && (
        <div className="video-channel-select-container">
          <div className="video-channel-select-wrapper">
            <a className="video-channel-select" onClick={() => setShowWrapper(true)} id="VideoUi_channelsButton">
              <h4>{translate['watchtv.channels']}</h4>
            </a>
          </div>
        </div>
      )}
      <Modal
        isFullscreen={isFullscreen}
        isShowing={showAudioModal}
        setIsShowing={setShowAudioModal}
        title={translate.modalAudioTitle}
        bodyButtons={audioOptions}
        buttons={[
          {
            id: 'ok',
            text: translate['modal.button.ok'],
            onClick: () => {
              updateAudioTrack(audioStreamId);
              setShowAudioModal(false);
            },
          },
        ]}
      />
      <Modal
        isFullscreen={isFullscreen}
        isShowing={showSubtitlesModal}
        setIsShowing={setShowSubtitlesModal}
        title={translate.modalSubtitleTitle}
        bodyButtons={subtitlesOptions}
        buttons={[
          {
            id: 'ok',
            text: translate['modal.button.ok'],
            onClick: () => {
              updateSelectedSubtitleTrack(activeSubtitleId);
              setShowSubtitlesModal(false);
            },
          },
        ]}
      />
      <div className="inner">
        <div
          className={`video-controls ${isFullscreen ? 'fullscreen' : ''} ${
            mediaData.controllers.disable || !(overlayActive && controllersActive) ? 'disabled' : ''
          } `}
        >
          <div className="video-transport-controls">
            <div className="video-controls-playback" id="VideoUi_controlsPlayback">
              <a
                className={`transport-control video-play-pause ${isPlaying ? 'pause' : 'play'} ${
                  !mediaData.controllers.play || (isPlaying && !mediaData.controllers.rules.allowPause)
                    ? 'disabled'
                    : ''
                }`}
                onClick={handleHotspot}
                id="VideoUi_controlsPlayback____play"
              />
              <a
                className={`transport-control video-start-over start-over ${
                  mediaData.controllers.restart ? '' : 'disabled'
                }`}
                onClick={handleRestartButton}
                id="VideoUi_controlsPlayback____restart"
              />
            </div>
            <div
              className={`video-controls-progress ${mediaData.controllers.subtitles ? '' : 'subtitles-disabled'}`}
              id="VideoUi_controlsProgress"
            >
              <VideoRangeSlider
                sliderActive={sliderActive}
                restartAllowed={mediaData.controllers.restart}
                moveScrubberAllowed={mediaData.controllers.moveScrubber}
                rules={mediaData.controllers.rules}
                handlePosition={handleScrubberPosition}
                duration={duration}
                progress={progress}
                liveProgress={liveProgress}
                seekingStream={seekingStream}
                onScrub={onScrub}
              />
              <div className="video-controls-data">
                <div className="video-controls-metadata">
                  {mediaData.channel && (
                    <div className={`channel-logo ${mediaData.channel.name ? '' : 'no-logo'}`} id="VideoUi_channelLogo">
                      <ImageLoader
                        src={mediaData.channel.logo}
                        fallbackComponent={
                          <span className="channel-name" id="VideoUi_channelName">
                            {mediaData.channel.name ? mediaData.channel.name : translate['player.noChannelName']}
                          </span>
                        }
                        bemModifier="playerChannelLogo"
                      />
                    </div>
                  )}
                  <div
                    className={`video-controls-name-container ${mediaData?.channel?.name ? '' : 'no-logo'} ${
                      !mediaData.channel ? 'no-channel' : ''
                    }`}
                  >
                    <div className="video-controls-name">
                      {mediaData.episode && mediaData.episode.name ? (
                        <span id="VideoUi_episodeName">{mediaData.episode.name}</span>
                      ) : null}
                      {!mediaData.episode ? (
                        <span id="VideoUi_episodeNameUnavailable">
                          {translate['watchtv.programInformationUnavailable']}
                        </span>
                      ) : null}

                      {(mediaData.episode.seasonNumber || mediaData.episode.episodeNumber) && (
                        <span className="video-controls-name-season-episode">
                          {mediaData.episode.seasonNumber && (
                            <>
                              <span id="VideoUi_seasonNumber">
                                {translate
                                  .formatString(translate['asset.seasonFormat'], mediaData.episode.seasonNumber)
                                  .toString()}
                              </span>
                              {mediaData.episode.episodeNumber && <span>,</span>}
                            </>
                          )}
                          {mediaData.episode.episodeNumber && (
                            <span id="VideoUi_episodeNumber">
                              {translate
                                .formatString(translate['asset.episodeFormat'], mediaData.episode.episodeNumber)
                                .toString()}
                            </span>
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="video-controls-counter">
                  <div className="video-controls-counter-container" id="VideoUi_controlsCounter">
                    <span className="time-progress" id="VideoUi_timeProgress">
                      {convertHMS(currentProgress.current)}
                    </span>
                    <span className="separator"> / </span>{' '}
                    <span className="endtime" id="VideoUi_endTime">
                      {convertHMS(duration)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="video-controls-viewing-options" id="VideoUi_controlsViewingOptions">
              {audioEnabled && nagraAudio?.length > 1 && (
                <a
                  className="transport-control accessibility audio"
                  onClick={() => {
                    setShowAudioModal(true);
                    setActiveModalType(EModalType.AUDIOTRACK);
                  }}
                  id="VideoUi_controlsViewingOptions___audio"
                />
              )}
              {audioEnabled && hasAudioDescription && (
                <a
                  className="transport-control accessibility audio-description"
                  onClick={() => toggleAudioDescriptionTrack()}
                  id="VideoUi_controlsViewingOptions___audioDescription"
                />
              )}
              {subtitlesEnabled && nagraSubtitles?.length > 0 && (
                <a
                  className="transport-control accessibility subtitles"
                  onClick={() => {
                    setShowSubtitlesModal(true);
                    setActiveModalType(EModalType.SUBTITLES);
                  }}
                  id="VideoUi_controlsViewingOptions___subtitles"
                />
              )}
              <VolumeController handleVolume={handleVolume} isMuted={isMuted} handleMute={handleMute} />
              <a
                className="transport-control video-full-screen full-screen"
                onClick={handleFullscreen}
                id="VideoUi_controlsViewingOptions___fullscreen"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default React.memo(VideoUi);
